// product-expansion-renderer.js - Offscreen rendering engine for expansion products
// Takes the hoodie's DESIGN_ITEMS + expansion config, produces rendered PNGs per view/color.

(function(window) {
  'use strict';

  var CANVAS_SIZE = 700;       // Display size
  var INTERNAL_SIZE = 1400;    // Render size (2x for resolution)
  var SCALE = INTERNAL_SIZE / CANVAS_SIZE;

  // =========================================================================
  // Utility: load an image as a promise
  // =========================================================================
  function loadImage(src) {
    return new Promise(function(resolve, reject) {
      if (!src) { resolve(null); return; }
      var img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = function() { resolve(img); };
      img.onerror = function() {
        console.warn("ExpansionRenderer: failed to load image", src);
        resolve(null);
      };
      img.src = src;
    });
  }

  // =========================================================================
  // Shared helpers: check hood mode/text for a side, accounting for
  // per-expansion overrides (EXPANSION_HOOD_MODE / EXPANSION_HOOD_TEXT)
  //
  // cardId is `versionId || productId`, the key the editor keeps a card's hood
  // under (ExpansionMode's getOrCopyHoodText). A duplicate (zip_hoodie__v1) has
  // its own; one with nothing for a side shows its original's, then the
  // hoodie's, as the editor does. Asked by the base id, a duplicate's card drew
  // the original's hood text.
  // =========================================================================
  function cardHoodValue(store, cardId, side) {
    if (!store || !cardId) return undefined;
    if (store[cardId] && store[cardId][side] !== undefined) return store[cardId][side];
    var baseId = window.VersionManager ? window.VersionManager.getBaseProductId(cardId) : cardId;
    if (baseId !== cardId && store[baseId]) return store[baseId][side];
    return undefined;
  }

  function hoodSideMode(side, cardId) {
    var expMode = cardHoodValue(window.EXPANSION_HOOD_MODE, cardId, side);
    if (expMode) return expMode;
    var fn = side === "left" ? "getHoodLeftMode" : "getHoodRightMode";
    return (window.ImageUpload && window.ImageUpload[fn] && window.ImageUpload[fn]()) || "image";
  }

  function isHoodSideTextMode(side, cardId) {
    return hoodSideMode(side, cardId) === "text";
  }

  function getHoodSideText(side, cardId) {
    var expText = cardHoodValue(window.EXPANSION_HOOD_TEXT, cardId, side);
    if (expText !== undefined && expText !== null) return String(expText).trim();
    return ($("#hood_" + side + "_text_input").val() || "").trim();
  }

  // =========================================================================
  // buildExpansionDesignItems
  // For each mapping in the config, find matching DESIGN_ITEMS from the hoodie
  // and create mapped items with destination box coordinates.
  // =========================================================================
  // Optional sourceImageOverrides: { boxName: Image } — when provided, creates a
  // shallow copy of sourceItem with the override image. This enables non-mutating
  // export where per-color logos are passed as overrides without touching DESIGN_ITEMS.
  // Optional expansionOverrides: { boxName: Image } — this product's OWN uploads,
  // keyed by destination box. A synthetic mapping (a box the config declares but
  // does not map, e.g. the t-shirt back) has no hoodie slot to inherit from, so
  // this is the only thing that can fill it.
  function buildExpansionDesignItems(config, sourceImageOverrides, mappings, expansionOverrides) {
    var DESIGN_ITEMS = window.ACTIVE_ITEMS || window.DESIGN_ITEMS || [];
    var mappedItems = [];

    (mappings || config.mappings).forEach(function(mapping) {
      var destBox = config.boxes[mapping.toBox];
      if (!destBox) return;

      // A synthetic mapping's source IS the customer's upload. With no artwork
      // for the box there is nothing to draw and nothing to inherit, so the slot
      // stays empty — which is the configured behaviour for an unmapped box.
      if (mapping.synthetic) {
        var ownImg = expansionOverrides && expansionOverrides[mapping.toBox];
        if (!ownImg) return;
        mappedItems.push({
          sourceItem: { img: ownImg, boxName: mapping.toBox, show: true, part_index: destBox.partIndex },
          mapping: mapping,
          destBox: destBox,
          productId: config.productId
        });
        return;
      }

      // Placeholder text mappings don't need a hoodie source item
      if (mapping.scaleMode === "placeholder_text") {
        mappedItems.push({
          sourceItem: null,
          mapping: mapping,
          destBox: destBox,
          productId: config.productId
        });
        return;
      }

      // Find source item on the hoodie
      var sourceItem = null;
      for (var i = 0; i < DESIGN_ITEMS.length; i++) {
        var item = DESIGN_ITEMS[i];
        if (item.show && item.boxName === mapping.from && item.part_index === mapping.fromPart) {
          sourceItem = item;
          break;
        }
      }

      if (!sourceItem) return; // No content in this hoodie area, skip

      // Which image this slot should actually draw. This product's OWN artwork
      // for the DESTINATION box wins; the hoodie's artwork for the SOURCE box is
      // the inheritance fallback, which is all this path used to consider.
      // Without the first rule the auto-map path drew the hoodie's design onto
      // every card that had no edited items — so a zip hoodie or a longsleeve
      // carrying its own upload previewed the hoodie's V1 logo, and only looked
      // right once its own editor had been opened.
      var overrideImg = (expansionOverrides && expansionOverrides[mapping.toBox]) ||
        (sourceImageOverrides && sourceImageOverrides[sourceItem.boxName]);

      // If we have an override image for this slot, use a shallow copy with the override
      var effectiveSource = sourceItem;
      if (overrideImg) {
        var ovW = overrideImg.naturalWidth || overrideImg.width || sourceItem.width;
        var ovH = overrideImg.naturalHeight || overrideImg.height || sourceItem.height;
        // scaleX is a multiplier on a PIXEL width, so it only means anything
        // paired with the width it was measured against. Substituting another
        // colour's artwork changes that width, and a "preserve" mapping then
        // draws it at (new pixel width x old scale), off by exactly the ratio
        // of the two files' widths. VJ's hoodie hit this after one colour was
        // re-uploaded at a different export size: the right hood carried a
        // 852 px scale onto a 3529 px navy file and drew 4.1x too big.
        // Correct the scale by the same ratio so the drawn size survives the
        // swap. One uniform factor for both axes, so nothing is distorted when
        // the two exports differ slightly in aspect.
        var sizeFix = (sourceItem.width > 0 && ovW > 0) ? sourceItem.width / ovW : 1;
        effectiveSource = {
          img: overrideImg,
          width: ovW,
          height: ovH,
          boxName: sourceItem.boxName,
          show: sourceItem.show,
          part_index: sourceItem.part_index,
          PO: sourceItem.PO,
          scaleX: (sourceItem.scaleX || 1) * sizeFix,
          scaleY: (sourceItem.scaleY || 1) * sizeFix,
          rotate: sourceItem.rotate
        };
      }

      mappedItems.push({
        sourceItem: effectiveSource,
        mapping: mapping,
        destBox: destBox,
        productId: config.productId
      });
    });

    return mappedItems;
  }

  // =========================================================================
  // expansionSlots / loadOwnBoxImages
  // The slots a product renders — its mappings, plus a synthetic one for every
  // box it declares that the customer has uploaded into — and the images that
  // fill those synthetic slots at a given colour.
  // =========================================================================
  /**
   * The slots this CARD actually draws, exclusions removed.
   *
   * Dropping them here rather than at each drawing site is deliberate: every
   * consumer (buildExpansionDesignItems, allBoxes, syntheticBoxes) reads its box
   * list from this one call, so a slot the customer emptied cannot come back
   * through a path nobody remembered to guard. It is the mockup's half of the
   * same rule DesignCollector applies to the design files — when the two
   * disagree, the review card shows a print that will never be produced, or
   * hides one that will.
   *
   * `color` is optional. With it, a removal made on one colourway only is
   * honoured for that colourway; without it, only card-wide removals are.
   */
  function expansionSlots(config, logoKey, color) {
    var key = logoKey || config.productId;
    var mappings;
    if (!window.ProductConfig || !window.ProductConfig.effectiveMappings) {
      mappings = config.mappings;
    } else {
      var filled = (window.VariantLogoUtil && window.VariantLogoUtil.uploadedBoxes)
        ? window.VariantLogoUtil.uploadedBoxes(key)
        : [];
      mappings = window.ProductConfig.effectiveMappings(config.productId, filled);
    }
    var VL = window.VariantLogoUtil;
    if (!VL || !VL.isExpansionSlotExcluded) return mappings;
    return (mappings || []).filter(function (m) {
      return !VL.isExpansionSlotExcluded(key, m.toBox, color);
    });
  }

  function syntheticBoxes(mappings) {
    return (mappings || []).filter(function (m) { return m.synthetic; })
      .map(function (m) { return m.toBox; });
  }

  /** Every destination box a set of slots can draw into. */
  function allBoxes(mappings) {
    return (mappings || []).map(function (m) { return m.toBox; })
      .filter(function (b, i, a) { return b && a.indexOf(b) === i; });
  }

  /**
   * Edited items for the boxes a product declares, has artwork for, and has no
   * item for — the t-shirt back being the case that forced this.
   *
   * The editor only ever creates an item from a mapping, so an upload into an
   * unmapped box produced artwork with nothing to position or draw it. These
   * items are placed by the box's own autoMap rule, exactly as a freshly
   * auto-mapped design would be, and only ever for boxes whose artwork the
   * customer explicitly uploaded.
   *
   * Excluded slots are skipped, same as DesignCollector skips them. Without that
   * the two disagreed about the same list: the collector wrote no design file
   * for an unchecked box while this built an item for it, so the mockup showed a
   * print that was never going to be produced — and warned about "missing"
   * artwork for a slot nobody wanted (the t-shirt back, Hefners 2026-08-14).
   */
  function syntheticEditedItems(config, logoKey, color, existingItems) {
    var slots = expansionSlots(config, logoKey);
    var have = {};
    (existingItems || []).forEach(function (it) { if (it && it.boxName) have[it.boxName] = true; });
    var excluded = (window.EXPANSION_EXCLUDED_SLOTS || {})[logoKey || config.productId] || [];
    var boxes = syntheticBoxes(slots).filter(function (b) {
      return !have[b] && excluded.indexOf(b) === -1;
    });
    if (!boxes.length) return Promise.resolve([]);
    return loadOwnBoxImages(logoKey, color, boxes).then(function (images) {
      var out = [];
      boxes.forEach(function (boxName) {
        var img = images[boxName];
        var destBox = config.boxes[boxName];
        if (!img || !destBox) return;
        var imgW = img.naturalWidth || img.width;
        var imgH = img.naturalHeight || img.height;
        if (!(imgW > 0) || !(imgH > 0)) return;
        var placement = (window.ProductConfig && window.ProductConfig.getAutoMapPlacement)
          ? window.ProductConfig.getAutoMapPlacement(config.productId, boxName, imgW, imgH)
          : null;
        if (!placement) {
          var s = Math.min(destBox.width / imgW, destBox.height / imgH);
          placement = {
            scale: s,
            centerX: destBox.x + destBox.width / 2,
            centerY: destBox.y + destBox.height / 2
          };
        }
        out.push({
          flag: 1, img: img, img_id: 0, show: true, selected: false,
          el_id: "synthetic_" + config.productId + "_" + boxName,
          boxName: boxName, box: boxName,
          part_index: destBox.partIndex,
          width: imgW, height: imgH,
          scaleX: placement.scale, scaleY: placement.scale, rotate: 0,
          PO: { x: placement.centerX, y: placement.centerY },
          synthetic: true
        });
      });
      return out;
    });
  }

  function loadOwnBoxImages(logoKey, color, boxNames) {
    if (!boxNames || !boxNames.length || !window.VariantLogoUtil) return Promise.resolve({});
    // Hydrate before resolving: an unhydrated S3 key reads as "no artwork" and
    // silently hands the slot back to the hoodie's design.
    return ensureExpansionArtwork(logoKey, color)
      .then(function () { return loadOwnBoxImagesNow(logoKey, color, boxNames); });
  }

  function loadOwnBoxImagesNow(logoKey, color, boxNames) {
    var logos = window.VariantLogoUtil.getExpansionLogosForColor(logoKey, color);
    var out = {};
    var loads = [];
    boxNames.forEach(function (box) {
      // A colourway with none of its own falls back to this product's other
      // colourway — the same order the collector and the print resolver use.
      var url = logos[box] || (window.VariantLogoUtil.siblingExpansionLogo
        ? window.VariantLogoUtil.siblingExpansionLogo(logoKey, color, box) : null);
      if (!url) {
        // The caller only asks for boxes the store says are filled, so an
        // unresolvable one means the artwork has not been hydrated yet. Say so:
        // the mockup will show a bare garment, and a silently bare back is
        // exactly what took a year to notice on the t-shirt.
        console.warn("[ExpansionRenderer] " + logoKey + " " + color + " " + box +
          ": artwork is in the store but not loaded — this slot will render empty");
        return;
      }
      loads.push(new Promise(function (resolve) {
        var img = new Image();
        img.onload = function () { out[box] = img; resolve(); };
        img.onerror = resolve;
        img.src = url;
      }));
    });
    if (!loads.length) return Promise.resolve({});
    return Promise.all(loads).then(function () { return out; });
  }

  // =========================================================================
  // renderSingleColumnTeamWall
  // Re-renders team wall names as a single vertical column for arm/sleeve.
  // Returns a data URL of the rendered text image.
  // =========================================================================
  // Thin wrapper: reads text from DOM and delegates to renderSingleColumnTeamWallFromText
  // Optional productId parameter uses per-expansion text when available.
  //
  // productId here is the CARD (versionId || productId), the same key the editor
  // keeps the list under (ExpansionMode.getOrCopyTeamWallText). A duplicate with
  // no list of its own draws its original's, then the hoodie's, as the editor does.
  function renderSingleColumnTeamWall(destBox, productId, textColor) {
    var store = window.EXPANSION_TEAMWALL_TEXT || {};
    var baseId = (productId && window.VersionManager) ? window.VersionManager.getBaseProductId(productId) : productId;
    var inputText = "";
    if (productId && store[productId]) {
      inputText = store[productId];
    } else if (baseId && store[baseId]) {
      inputText = store[baseId];
    } else {
      inputText = $("#back_middle_input").val() || "";
    }
    return renderSingleColumnTeamWallFromText(inputText, destBox, textColor);
  }

  // =========================================================================
  // renderExpansionView
  // Draws bg template, renders mapped design items, overlays transparency,
  // returns PNG data URL.
  // =========================================================================
  // Use shared drawCentered from CanvasModule
  var drawCentered = window.CanvasModule.drawCentered;

  // cardId: the card whose sleeve name list and hood text are drawn (a
  // duplicate's versionId). Omitted, it is productId, as before.
  function renderExpansionView(viewName, config, colorData, mappedItems, productId, textColor, cardId) {
    // Wrap in timeout to prevent hanging forever if an image fails to load
    var renderPromise = new Promise(function(resolve) {
      try {
      var exportCanvas = document.createElement("canvas");
      exportCanvas.width = INTERNAL_SIZE;
      exportCanvas.height = INTERNAL_SIZE;
      var ex = exportCanvas.getContext("2d");

      // 1. Draw background template (centered, maintaining aspect ratio)
      var bgUrl = colorData[viewName + "_bg"] || "";
      var transUrl = colorData[viewName + "_trans"] || "";

      loadImage(bgUrl).then(function(bgImg) {
        if (bgImg) {
          drawCentered(ex, bgImg, INTERNAL_SIZE);
        }

        // 2. Draw mapped design items for this view
        var viewMappings = mappedItems.filter(function(m) {
          return m.mapping.toView === viewName;
        });

        var teamWallPromise = Promise.resolve();

        // Helper to detect hood-text mappings that need fresh rendering
        var isHoodText = function(mapping) {
          if (mapping.scaleMode !== "fit" && mapping.scaleMode !== "preserve") return false;
          if (mapping.from !== "hood_left" && mapping.from !== "hood_right") return false;
          var side = mapping.from === "hood_left" ? "left" : "right";
          if (!isHoodSideTextMode(side, cardId || productId)) return false;
          return getHoodSideText(side, cardId || productId);
        };

        viewMappings.forEach(function(mapped) {
          if (mapped.mapping.scaleMode === "reformat_teamwall" || mapped.mapping.scaleMode === "placeholder_text") {
            // Will handle async below
            return;
          }
          // Skip hood text items — will re-render async with correct color
          if (isHoodText(mapped.mapping)) {
            return;
          }
          drawMappedItem(ex, mapped);
        });

        // Handle team wall mapping (async - needs re-rendering)
        var teamWallMapping = viewMappings.find(function(m) {
          return m.mapping.scaleMode === "reformat_teamwall";
        });

        if (teamWallMapping) {
          teamWallPromise = renderSingleColumnTeamWall(teamWallMapping.destBox, cardId || productId, textColor)
            .then(function(teamWallCanvas) {
              if (teamWallCanvas) {
                var box = teamWallMapping.destBox;
                ex.drawImage(
                  teamWallCanvas,
                  0, 0, teamWallCanvas.width, teamWallCanvas.height,
                  box.x * SCALE, box.y * SCALE,
                  box.width * SCALE, box.height * SCALE
                );
              }
            });
        }

        // Handle placeholder text mappings (async - needs font loading)
        var placeholderMappings = viewMappings.filter(function(m) {
          return m.mapping.scaleMode === "placeholder_text";
        });

        var placeholderPromise = teamWallPromise;
        placeholderMappings.forEach(function(mapped) {
          placeholderPromise = placeholderPromise.then(function() {
            return renderPlaceholderText(mapped.mapping.placeholderText, mapped.destBox, textColor)
              .then(function(placeholderCanvas) {
                if (placeholderCanvas) {
                  var box = mapped.destBox;
                  ex.drawImage(
                    placeholderCanvas,
                    0, 0, placeholderCanvas.width, placeholderCanvas.height,
                    box.x * SCALE, box.y * SCALE,
                    box.width * SCALE, box.height * SCALE
                  );
                }
              });
          });
        });

        // Handle hood text mappings (async - re-render with correct color, proportional size)
        var hoodTextMappings = viewMappings.filter(function(m) {
          return isHoodText(m.mapping);
        });
        hoodTextMappings.forEach(function(mapped) {
          placeholderPromise = placeholderPromise.then(function() {
            var side = mapped.mapping.from === "hood_left" ? "left" : "right";
            var hoodText = getHoodSideText(side, cardId || productId).toUpperCase();
            // Compute offset from source item's dragged position relative to hoodie hood box center
            var offset = { x: 0, y: 0 };
            if (mapped.sourceItem && mapped.sourceItem.PO) {
              var sourceBox = window.ProductConfig
                ? window.ProductConfig.getBoxByName("hoodie", mapped.mapping.from)
                : null;
              if (!sourceBox) sourceBox = { x: 0, y: 0, width: 120, height: 120 };
              offset.x = mapped.sourceItem.PO.x - (sourceBox.x + sourceBox.width / 2);
              offset.y = mapped.sourceItem.PO.y - (sourceBox.y + sourceBox.height / 2);
            }
            return renderHoodTextTight(hoodText, mapped.destBox, textColor, offset, mapped.mapping.from)
              .then(function(result) {
                if (result) {
                  ex.drawImage(
                    result.canvas,
                    0, 0, result.canvas.width, result.canvas.height,
                    result.drawX, result.drawY, result.drawW, result.drawH
                  );
                }
              });
          });
        });

        placeholderPromise.then(function() {
          // 3. Draw transparency overlay
          if (transUrl) {
            loadImage(transUrl).then(function(transImg) {
              if (transImg) {
                drawCentered(ex, transImg, INTERNAL_SIZE);
              }
              resolve(exportCanvas.toDataURL("image/png"));
            });
          } else {
            resolve(exportCanvas.toDataURL("image/png"));
          }
        }).catch(function(err) {
          console.error("renderExpansionView: chain rejected for " + productId + " " + viewName, err);
          resolve(null);
        });
      }).catch(function(err) {
        console.error("renderExpansionView: bg chain rejected for " + productId + " " + viewName, err);
        resolve(null);
      });
      } catch (e) {
        console.error("renderExpansionView: sync throw for " + productId + " " + viewName, e);
        resolve(null);
      }
    });
    // 30-second timeout to prevent hanging when images fail to load. The timer is
    // cleared once the render settles so it never fires a misleading "timeout" log
    // ~30s after a render that actually succeeded. Without the clear, a full
    // expansion pass printed one console.error per product per view for work that
    // completed fine, which is what made the real failures impossible to spot.
    var timeoutTimer = null;
    var timeoutPromise = new Promise(function(resolve) {
      timeoutTimer = setTimeout(function() {
        console.error("renderExpansionView: timeout for " + productId + " " + viewName);
        resolve(null);
      }, 30000);
    });
    return Promise.race([renderPromise, timeoutPromise]).then(function(res) {
      if (timeoutTimer) { clearTimeout(timeoutTimer); timeoutTimer = null; }
      return res;
    });
  }

  // =========================================================================
  // drawMappedItem
  // Draw a single design item (image) scaled to fit a destination box.
  // Never clips/crops - the full uploaded design is always visible.
  // =========================================================================
  function drawMappedItem(ctx, mapped) {
    var item = mapped.sourceItem;
    var destBox = mapped.destBox;

    if (!item || !item.img) return;

    // Calculate scale to fit destination box (use naturalWidth for Image elements)
    var imgW = item.img.naturalWidth || item.img.width;
    var imgH = item.img.naturalHeight || item.img.height;
    if (imgW <= 0 || imgH <= 0) return;

    // scaleMode "preserve": keep the user's in-box scale and position from the
    // source product. The item's offset from the SOURCE box center is transposed
    // onto the dest box center (source and dest boxes have identical dimensions
    // for hood mappings, so the result always stays in-box).
    if (mapped.mapping && mapped.mapping.scaleMode === "preserve" && item.PO) {
      var srcBox = window.ProductConfig
        ? window.ProductConfig.getBoxByName("hoodie", mapped.mapping.from)
        : null;
      if (srcBox) {
        var frameW = item.width || imgW;
        var frameH = item.height || imgH;
        var pW = frameW * (item.scaleX || 1) * SCALE;
        var pH = frameH * (item.scaleY || 1) * SCALE;
        var offX = item.PO.x - (srcBox.x + srcBox.width / 2);
        var offY = item.PO.y - (srcBox.y + srcBox.height / 2);
        var cx = (destBox.x + destBox.width / 2 + offX) * SCALE;
        var cy = (destBox.y + destBox.height / 2 + offY) * SCALE;
        ctx.save();
        ctx.translate(cx, cy);
        // Hood boxes clamp to 0 here too: an expansion card inherits the
        // hoodie's hood badge as back_hood_left/back_hood_right, and this
        // renderer is the other canvas that turns an item into print pixels.
        var rot = window.DesignHelpers.drawRotation(item);
        if (rot) ctx.rotate(rot);
        ctx.drawImage(item.img, 0, 0, imgW, imgH, -pW / 2, -pH / 2, pW, pH);
        ctx.restore();
        return;
      }
    }

    // Size and position come from the box's autoMap rule (print area, collar
    // offset, corner alignment). Boxes with no rule get the historical
    // behaviour: fitted to the whole box, centred, never cropped.
    var placement = (window.ProductConfig && window.ProductConfig.getAutoMapPlacement && mapped.productId)
      ? window.ProductConfig.getAutoMapPlacement(mapped.productId, mapped.mapping.toBox, imgW, imgH)
      : null;
    if (!placement) {
      var s = Math.min(destBox.width / imgW, destBox.height / imgH);
      placement = {
        scale: s,
        centerX: destBox.x + destBox.width / 2,
        centerY: destBox.y + destBox.height / 2
      };
    }

    var drawW = imgW * placement.scale * SCALE;
    var drawH = imgH * placement.scale * SCALE;
    var drawX = placement.centerX * SCALE - drawW / 2;
    var drawY = placement.centerY * SCALE - drawH / 2;

    // Draw without clipping - the full design is always preserved
    ctx.drawImage(item.img, 0, 0, imgW, imgH, drawX, drawY, drawW, drawH);
  }

  // =========================================================================
  // exportProductImages
  // Loops over color variants, renders all views, returns:
  //   { "Grå": { front: dataUrl, back: dataUrl, arm: dataUrl }, ... }
  // =========================================================================
  // logoKey: which EXPANSION_VARIANT_LOGOS entry holds this CARD's uploads, the
  // same contract as exportPreviewForCurrentColor below. A duplicate renders the
  // base product's geometry but its OWN artwork, so it passes its versionId.
  //
  // It could not, until now — the parameter did not exist, and both callers pass
  // ver.baseProductId. So a duplicate with no editedItems of its own had its
  // review card rendered from the BASE card's store: literally the original's
  // artwork on the duplicate's card, which is one of the ways removed artwork
  // "came back" at review. Omitted, it still defaults to productId, so every
  // plain expansion product behaves exactly as before.
  function exportProductImages(productId, logoKey) {
    var config = window.ExpansionConfig.getProduct(productId);
    if (!config) {
      console.error("ExpansionRenderer: no config for", productId);
      return Promise.resolve({});
    }
    var artKey = logoKey || productId;

    var variantData = window.EXPANSION_VARIANT_DATA && window.EXPANSION_VARIANT_DATA[productId];
    if (!variantData) {
      console.warn("ExpansionRenderer: no variant data for", productId);
      return Promise.resolve({});
    }

    var results = {};
    // Only export colors that actually exist for this product
    var productColors = Object.keys(variantData);
    var hoodieColors = window.variantData
      ? [...new Set(window.variantData.map(function(v) { return v.color; }))]
      : [];
    var colors = hoodieColors.filter(function(c) { return productColors.indexOf(c) !== -1; });
    if (colors.length === 0) colors = productColors;
    // Single-product flow: window.variantData is the PAGE product, not a hoodie,
    // so it must NOT gate an added/duplicated garment's color set. Render every
    // mockup color the garment has; the single flow filters to the user's chosen
    // colors downstream (filterImagesToColors + job.colorAllowlist).
    if (window.SINGLE_PRODUCT && window.SINGLE_PRODUCT.enabled) colors = productColors;

    return colors.reduce(function(chain, color) {
      return chain.then(function() {
        var colorData = variantData[color];
        // Fall back to Grå template, then first available color if this color has no data
        if (!colorData) colorData = variantData["Grå"];
        if (!colorData) colorData = variantData[Object.keys(variantData)[0]];
        if (!colorData) {
          console.warn("ExpansionRenderer: skipping color", color, "- no template data");
          return Promise.resolve();
        }
        // Check if any view has a bg image
        var hasAnyBg = config.views.some(function(vn) { return colorData[vn + "_bg"]; });
        if (!hasAnyBg) {
          colorData = variantData["Grå"];
          if (!colorData) colorData = variantData[Object.keys(variantData)[0]];
          if (!colorData) return Promise.resolve();
        }

        results[color] = {};

        // Determine text color for this variant and pass explicitly
        var variantTextColor = window.VariantLogoUtil.getTextColor(color);

        // NON-MUTATING: Load hoodie logo images for this color as overrides
        var logoLoadPromise = Promise.resolve({});
        if (window.CanvasExporter && window.CanvasExporter.loadLogoImagesForColor) {
          logoLoadPromise = window.CanvasExporter.loadLogoImagesForColor(color);
        }

        var slots = expansionSlots(config, artKey, color);
        return logoLoadPromise.then(function(logoOverrides) {
          return loadOwnBoxImages(artKey, color, allBoxes(slots)).then(function(ownImages) {
          // Build mapped items with per-color overrides — DESIGN_ITEMS are NOT mutated
          var mappedItems = buildExpansionDesignItems(config, logoOverrides, slots, ownImages);

          return config.views.reduce(function(viewChain, viewName) {
            return viewChain.then(function() {
              return renderExpansionView(viewName, config, colorData, mappedItems, productId, variantTextColor, artKey)
                .then(function(dataUrl) {
                  results[color][viewName] = dataUrl;
                });
            });
          }, Promise.resolve());
          });
        });
      });
    }, Promise.resolve()).then(function() {
      console.log("ExpansionRenderer: exported images for", artKey, Object.keys(results));
      return results;
    });
  }

  // The garment colour a white-only card is previewed in (VariantLogoUtil
  // garmentColorsFor / shownGarmentColor), or null for every other card. A
  // requested colour wins when the card's rows are ordered in it.
  function garmentColorFor(cardId, requested) {
    var VLU = window.VariantLogoUtil;
    if (!VLU || !VLU.garmentColorCard || !VLU.garmentColorCard(cardId)) return null;
    if (requested) {
      var want = VLU.garmentColorOf(requested);
      if (VLU.garmentColorsFor(cardId).indexOf(want) !== -1) return want;
    }
    return VLU.shownGarmentColor(cardId);
  }

  // =========================================================================
  // exportPreviewForCurrentColor
  // Quick render of all views for the currently selected color only.
  // Used for the preview overlay before confirming creation.
  // =========================================================================
  // logoKey: which EXPANSION_VARIANT_LOGOS entry holds this CARD's uploads. A
  // duplicate renders the base product's geometry but its own artwork, so it
  // passes its versionId. Omitted, it defaults to productId — which for a
  // duplicate would preview the artwork of the card it was copied from.
  function exportPreviewForCurrentColor(productId, logoKey) {
    var config = window.ExpansionConfig.getProduct(productId);
    if (!config) return Promise.resolve(null);

    var variantData = window.EXPANSION_VARIANT_DATA && window.EXPANSION_VARIANT_DATA[productId];
    if (!variantData) return Promise.resolve(null);

    // Determine current color from the color picker, fall back by position then first available
    var currentColor = $(".canvas-color-picker .color-btn.selected").data("title") || "Grå";
    var colorData = variantData[currentColor];
    if (!colorData) {
      var hoodieColors = [...new Set(window.variantData.map(function(v) { return v.color; }))];
      var expColors = Object.keys(variantData);
      var cidx = hoodieColors.indexOf(currentColor);
      if (cidx >= 0 && cidx < expColors.length) {
        currentColor = expColors[cidx];
        colorData = variantData[currentColor];
      }
    }
    if (!colorData) colorData = variantData[Object.keys(variantData)[0]];
    if (!colorData) return Promise.resolve(null);
    var hasAnyBg = config.views.some(function(vn) { return colorData[vn + "_bg"]; });
    if (!hasAnyBg) {
      colorData = variantData[Object.keys(variantData)[0]];
      if (!colorData) return Promise.resolve(null);
    }

    // A white-only card is drawn in the garment colour of its rows, never the
    // hoodie swatch; currentColor stays the hoodie colour its artwork inherits.
    var garment = garmentColorFor(logoKey || productId);
    if (garment) {
      colorData = window.VariantLogoUtil.garmentMockup(productId, garment) || colorData;
      currentColor = window.VariantLogoUtil.garmentArtColor();
    }

    var result = {};
    var textColor = window.VariantLogoUtil.getTextColor(garment || currentColor);

    // Load per-color hoodie logos as overrides (matching exportProductImages pattern)
    var logoLoadPromise = Promise.resolve({});
    if (window.CanvasExporter && window.CanvasExporter.loadLogoImagesForColor) {
      logoLoadPromise = window.CanvasExporter.loadLogoImagesForColor(currentColor);
    }

    var ownKey = logoKey || productId;
    var slots = expansionSlots(config, ownKey, currentColor);
    return logoLoadPromise.then(function(logoOverrides) {
      return loadOwnBoxImages(ownKey, currentColor, allBoxes(slots)).then(function(ownImages) {
        var mappedItems = buildExpansionDesignItems(config, logoOverrides, slots, ownImages);

        return config.views.reduce(function(chain, viewName) {
          return chain.then(function() {
            return renderExpansionView(viewName, config, colorData, mappedItems, productId, textColor, ownKey)
              .then(function(dataUrl) {
                result[viewName] = dataUrl;
              });
          });
        }, Promise.resolve()).then(function() {
          return result;
        });
      });
    });
  }

  // =========================================================================
  // drawStretched
  // Draws image to fill canvas. For square images, stretches to fill.
  // For non-square images, centers while preserving aspect ratio.
  // =========================================================================
  function drawStretched(ctx, img, canvasSize) {
    var imgW = img.naturalWidth || img.width;
    var imgH = img.naturalHeight || img.height;
    var isSquare = Math.abs(imgW / imgH - 1) < 0.05;
    if (isSquare) {
      ctx.drawImage(img, 0, 0, imgW, imgH, 0, 0, canvasSize, canvasSize);
    } else {
      drawCentered(ctx, img, canvasSize);
    }
  }

  // =========================================================================
  // renderViewFromEditedItems
  // Renders a single view using user-edited EXPANSION_DESIGN_ITEMS positions.
  // Uses stretch-to-fill for templates (matching the editor canvas) and
  // no clipping on designs so the preview matches the editor exactly.
  // =========================================================================
  // Optional imageOverrides: { boxName: Image } — when provided, uses the override
  // image for matching items instead of item.img. Enables non-mutating export.
  // Optional cardId: whose sleeve name list and hood text to draw, as in
  // renderExpansionView.
  function renderViewFromEditedItems(viewName, config, colorData, zipItems, productId, textColor, imageOverrides, cardId) {
    var renderPromise = new Promise(function(resolve) {
      try {
      var exportCanvas = document.createElement("canvas");
      exportCanvas.width = INTERNAL_SIZE;
      exportCanvas.height = INTERNAL_SIZE;
      var ex = exportCanvas.getContext("2d");

      var viewIndex = config.views.indexOf(viewName);
      var bgUrl = colorData[viewName + "_bg"] || "";
      var transUrl = colorData[viewName + "_trans"] || "";

      loadImage(bgUrl).then(function(bgImg) {
        if (bgImg) {
          drawStretched(ex, bgImg, INTERNAL_SIZE);
        }

        // Draw each zip item for this view (no clipping — matches editor)
        var viewItems = zipItems.filter(function(item) {
          return item.show && item.part_index === viewIndex;
        });

        // Helper to detect hood-text items that need fresh rendering
        var isHoodTextItem = function(item) {
          if (!item.sourceMapping) return false;
          if (item.sourceMapping.scaleMode !== "fit" && item.sourceMapping.scaleMode !== "preserve") return false;
          var from = item.sourceMapping.from;
          if (from !== "hood_left" && from !== "hood_right") return false;
          var side = from === "hood_left" ? "left" : "right";
          if (!isHoodSideTextMode(side, cardId || productId)) return false;
          return getHoodSideText(side, cardId || productId);
        };

        // Build set of teamwall destination box names so we can skip stale pre-rendered items
        var teamwallBoxNames = {};
        if (config.mappings) {
          config.mappings.forEach(function(m) {
            if (m.scaleMode === "reformat_teamwall" && m.toView === viewName) {
              teamwallBoxNames[m.toBox] = true;
            }
          });
        }

        viewItems.forEach(function(item) {
          // Use override image if available, otherwise use item.img
          var drawImg = (imageOverrides && imageOverrides[item.boxName]) || item.img;
          if (!drawImg) return;
          if (item.width <= 0 || item.height <= 0) return;
          // Skip hood text items — will re-render with correct color below
          if (isHoodTextItem(item)) return;
          // Skip teamwall items — will re-render fresh with correct textColor below
          if (item.boxName && teamwallBoxNames[item.boxName]) return;

          // Use the item's edited PO, scale and rotation directly (matches
          // editor drawImages). Drawn about the centre so the rotation the
          // customer set on the card is the rotation the mockup shows; the
          // hood clamp rides along through drawRotation like every other
          // canvas that rasterises an item.
          var drawW = item.width * item.scaleX * SCALE;
          var drawH = item.height * item.scaleY * SCALE;

          // Use actual image dimensions for source rect to avoid cropping when override
          // image has different dimensions than item.width/height
          var srcW = drawImg.naturalWidth || drawImg.width || item.width;
          var srcH = drawImg.naturalHeight || drawImg.height || item.height;
          ex.save();
          ex.translate(item.PO.x * SCALE, item.PO.y * SCALE);
          var editedRot = window.DesignHelpers.drawRotation(item);
          if (editedRot) ex.rotate(editedRot);
          ex.drawImage(drawImg, 0, 0, srcW, srcH, -drawW / 2, -drawH / 2, drawW, drawH);
          ex.restore();
        });

        // Render placeholder text from config mappings (e.g. "ETTERNAVN" on zip hoodie)
        var placeholderPromise = Promise.resolve();
        if (config.mappings) {
          var phMappings = config.mappings.filter(function(m) {
            return m.scaleMode === "placeholder_text" && m.toView === viewName;
          });
          phMappings.forEach(function(mapping) {
            var destBox = config.boxes[mapping.toBox];
            if (!destBox) return;
            placeholderPromise = placeholderPromise.then(function() {
              return renderPlaceholderText(mapping.placeholderText, destBox, textColor)
                .then(function(phCanvas) {
                  if (phCanvas) {
                    ex.drawImage(
                      phCanvas,
                      0, 0, phCanvas.width, phCanvas.height,
                      destBox.x * SCALE, destBox.y * SCALE,
                      destBox.width * SCALE, destBox.height * SCALE
                    );
                  }
                });
            });
          });

          // Handle hood text mappings (re-render with correct color, proportional size)
          var hoodTextMappings = config.mappings.filter(function(m) {
            if (m.scaleMode !== "fit" && m.scaleMode !== "preserve") return false;
            if (m.toView !== viewName) return false;
            if (m.from !== "hood_left" && m.from !== "hood_right") return false;
            var side = m.from === "hood_left" ? "left" : "right";
            if (!isHoodSideTextMode(side, cardId || productId)) return false;
            return getHoodSideText(side, cardId || productId);
          });
          hoodTextMappings.forEach(function(mapping) {
            var destBox = config.boxes[mapping.toBox];
            if (!destBox) return;
            placeholderPromise = placeholderPromise.then(function() {
              var side = mapping.from === "hood_left" ? "left" : "right";
              var hoodText = getHoodSideText(side, cardId || productId).toUpperCase();
              // Find the edited item for this hood text box to get user's dragged position
              var editedItem = viewItems.find(function(it) { return it.boxName === mapping.toBox; });
              var offset = { x: 0, y: 0 };
              if (editedItem && editedItem.PO) {
                offset.x = editedItem.PO.x - (destBox.x + destBox.width / 2);
                offset.y = editedItem.PO.y - (destBox.y + destBox.height / 2);
              }
              return renderHoodTextTight(hoodText, destBox, textColor, offset, mapping.from)
                .then(function(result) {
                  if (result) {
                    ex.drawImage(
                      result.canvas,
                      0, 0, result.canvas.width, result.canvas.height,
                      result.drawX, result.drawY, result.drawW, result.drawH
                    );
                  }
                });
            });
          });

          // Handle teamwall mappings — re-render from per-product text
          // (items may have been skipped during restore because sourceFrom === "back_middle")
          var twMappings = config.mappings.filter(function(m) {
            return m.scaleMode === "reformat_teamwall" && m.toView === viewName;
          });
          twMappings.forEach(function(mapping) {
            var destBox = config.boxes[mapping.toBox];
            if (!destBox) return;
            placeholderPromise = placeholderPromise.then(function() {
              return renderSingleColumnTeamWall(destBox, cardId || productId, textColor)
                .then(function(twCanvas) {
                  if (twCanvas) {
                    ex.drawImage(
                      twCanvas,
                      0, 0, twCanvas.width, twCanvas.height,
                      destBox.x * SCALE, destBox.y * SCALE,
                      destBox.width * SCALE, destBox.height * SCALE
                    );
                  }
                });
            });
          });
        }

        placeholderPromise.then(function() {
          // Draw transparency overlay (stretch to match editor)
          if (transUrl) {
            loadImage(transUrl).then(function(transImg) {
              if (transImg) {
                drawStretched(ex, transImg, INTERNAL_SIZE);
              }
              resolve(exportCanvas.toDataURL("image/png"));
            });
          } else {
            resolve(exportCanvas.toDataURL("image/png"));
          }
        }).catch(function(err) {
          console.error("renderViewFromEditedItems: chain rejected for " + productId + " " + viewName, err);
          resolve(null);
        });
      }).catch(function(err) {
        console.error("renderViewFromEditedItems: bg chain rejected for " + productId + " " + viewName, err);
        resolve(null);
      });
      } catch (e) {
        console.error("renderViewFromEditedItems: sync throw for " + productId + " " + viewName, e);
        resolve(null);
      }
    });
    // 30-second timeout to prevent hanging when images fail to load. The timer is
    // cleared once the render settles so it never fires a misleading "timeout" log
    // ~30s after a render that actually succeeded.
    var timeoutTimer = null;
    var timeoutPromise = new Promise(function(resolve) {
      timeoutTimer = setTimeout(function() {
        console.error("renderViewFromEditedItems: timeout for " + productId + " " + viewName);
        resolve(null);
      }, 30000);
    });
    return Promise.race([renderPromise, timeoutPromise]).then(function(res) {
      if (timeoutTimer) { clearTimeout(timeoutTimer); timeoutTimer = null; }
      return res;
    });
  }

  // =========================================================================
  // buildEditedItemOverrides
  // Builds per-color image overrides for expansion edited items.
  // Only emits an override when EXPANSION_VARIANT_LOGOS has an explicit
  // per-product per-color upload. Items without one keep their item.img
  // (set during edit-mode restore from manifest baseItems), so per-product
  // placement edits don't visually revert to the hoodie auto-map.
  // Returns Promise<{ boxName: Image }>.
  // =========================================================================
  function buildEditedItemOverrides(productId, color, items, config, logoKey) {
    if (!window.VariantLogoUtil) return Promise.resolve({});
    // Artwork loads lazily, and an unhydrated S3 key resolves to null — which
    // reads here as "this product has no design of its own" and leaves the item
    // showing whatever is already in item.img. After an edit-mode restore that
    // is the HOODIE's design file, so the card renders the hoodie's artwork
    // until something else happens to hydrate the key. Fetch first, so the
    // answer is about what the customer uploaded and never about download
    // timing. Siblings included: the resolver falls back to this product's other
    // colourway before the hoodie's, so leaving those unhydrated re-opens the
    // same hole one level down.
    return ensureExpansionArtwork(logoKey || productId, color)
      .then(function () { return buildEditedItemOverridesNow(productId, color, items, config, logoKey); });
  }

  function ensureExpansionArtwork(logoKey, color) {
    var VLU = window.VariantLogoUtil;
    if (!VLU || !window.LogoStore || !VLU.keysForExpansion) return Promise.resolve();
    var keys = VLU.keysForExpansion(logoKey, color);
    if (VLU.keysForExpansionSiblings) {
      keys = keys.concat(VLU.keysForExpansionSiblings(logoKey, color));
    }
    keys = keys.filter(function (k, i, a) { return a.indexOf(k) === i; });
    if (!keys.length) return Promise.resolve();
    return window.LogoStore.ensure(keys).catch(function () { /* render anyway */ });
  }

  function buildEditedItemOverridesNow(productId, color, items, config, logoKey) {
    // Use getExpansionLogosForColor for color normalization + cross-color fallback
    var expColorLogos = window.VariantLogoUtil.getExpansionLogosForColor(logoKey || productId, color);

    var overrideMap = {};
    var loadPromises = [];

    items.forEach(function(item) {
      if (!item.show) return;
      if (item.sourceMapping && (item.sourceMapping.scaleMode === "reformat_teamwall" || item.sourceMapping.scaleMode === "placeholder_text")) return;
      if (item.isPlaceholder) return;
      if (item.sourceMapping && (item.sourceMapping.from === "hood_left" || item.sourceMapping.from === "hood_right")) {
        var side = item.sourceMapping.from === "hood_left" ? "left" : "right";
        if (isHoodSideTextMode(side, logoKey || productId)) return;
      }

      // Only override when there's an actual per-product per-color upload
      // (EXPANSION_VARIANT_LOGOS). Without this guard, restored items would
      // have their item.img clobbered by VARIANT_LOGOS[color][BOX_TO_KEY[from]],
      // so any expansion-editor placement edit on a slot sourced from hoodie
      // would visually revert to the auto-mapped hoodie artwork in the preview.
      //
      // A colourway with none of its own takes THIS product's artwork from
      // another colourway before it takes the hoodie's — the order the collector
      // (siblingColorLogo) and the print resolver both use. Leaving it out is
      // what let a zip hoodie or a longsleeve preview the hoodie's design while
      // the print shop got the customer's.
      var newDataUrl = expColorLogos[item.boxName] ||
        (window.VariantLogoUtil.siblingExpansionLogo
          ? window.VariantLogoUtil.siblingExpansionLogo(logoKey || productId, color, item.boxName)
          : null);

      if (newDataUrl) {
        (function(boxName, url) {
          loadPromises.push(new Promise(function(resolve) {
            var img = new Image();
            img.onload = function() { overrideMap[boxName] = img; resolve(); };
            img.onerror = resolve;
            img.src = url;
          }));
        })(item.boxName, newDataUrl);
      }
    });

    if (loadPromises.length === 0) return Promise.resolve({});
    return Promise.all(loadPromises).then(function() { return overrideMap; });
  }

  // =========================================================================
  // exportFromEditedItems
  // Renders final product images using user-edited item positions
  // instead of auto-mapping from hoodie items.
  // editedItems param is optional: falls back to EXPANSION_EDITED_ITEMS[productId]
  // or the open editor's EXPANSION_DESIGN_ITEMS.
  // =========================================================================
  function exportFromEditedItems(productId, editedItems, logoKey) {
    var config = window.ExpansionConfig.getProduct(productId);
    if (!config) return Promise.resolve({});

    var variantData = window.EXPANSION_VARIANT_DATA && window.EXPANSION_VARIANT_DATA[productId];
    if (!variantData) return Promise.resolve({});

    var items = editedItems
      || (window.EXPANSION_EDITED_ITEMS && window.EXPANSION_EDITED_ITEMS[productId])
      || window.EXPANSION_DESIGN_ITEMS
      || [];
    // A product whose only artwork is in an unmapped box has no items at all,
    // and bailing here would render nothing for it. Its slots are synthesized
    // per colour below, so the upload alone is enough to have something to draw.
    if (!items.length && !syntheticBoxes(expansionSlots(config, logoKey || productId)).length) {
      return Promise.resolve({});
    }

    var results = {};
    // Only export colors that actually exist for this product
    var productColors = Object.keys(variantData);
    var hoodieColors = window.variantData
      ? [...new Set(window.variantData.map(function(v) { return v.color; }))]
      : [];
    var colors = hoodieColors.filter(function(c) { return productColors.indexOf(c) !== -1; });
    if (colors.length === 0) colors = productColors;
    // Single-product flow: window.variantData is the PAGE product, not a hoodie,
    // so it must NOT gate an added/duplicated garment's color set. Render every
    // mockup color the garment has; the single flow filters to the user's chosen
    // colors downstream (filterImagesToColors + job.colorAllowlist).
    if (window.SINGLE_PRODUCT && window.SINGLE_PRODUCT.enabled) colors = productColors;

    return colors.reduce(function(chain, color) {
      return chain.then(function() {
        var colorData = variantData[color];
        if (!colorData) colorData = variantData["Grå"];
        if (!colorData) colorData = variantData[Object.keys(variantData)[0]];
        if (!colorData) return Promise.resolve();

        // Check if this color has any bg image at all
        var hasAnyBg = config.views.some(function(vn) { return colorData[vn + "_bg"]; });
        if (!hasAnyBg) {
          colorData = variantData["Grå"];
          if (!colorData) colorData = variantData[Object.keys(variantData)[0]];
          if (!colorData) return Promise.resolve();
        }

        results[color] = {};

        // Determine text color for this variant and pass explicitly
        var variantTextColor = window.VariantLogoUtil.getTextColor(color);

        // NON-MUTATING: Build image overrides for this color instead of mutating item.img
        return syntheticEditedItems(config, logoKey || productId, color, items).then(function(extraItems) {
        var colorItems = extraItems.length ? items.concat(extraItems) : items;
        return buildEditedItemOverrides(productId, color, colorItems, config, logoKey).then(function(imageOverrides) {
          return config.views.reduce(function(viewChain, viewName) {
            return viewChain.then(function() {
              return renderViewFromEditedItems(viewName, config, colorData, colorItems, productId, variantTextColor, imageOverrides, logoKey)
                .then(function(dataUrl) {
                  results[color][viewName] = dataUrl;
                });
            });
          }, Promise.resolve());
        });
        });
      });
    }, Promise.resolve()).then(function() {
      // No item restoration needed — items were never mutated
      console.log("ExpansionRenderer: exported edited images for", productId, Object.keys(results));
      return results;
    });
  }

  // =========================================================================
  // reRenderExpansionPreview
  // Quick render of all views for the currently selected color using
  // edited item positions (after user editing). Generic for any product.
  // =========================================================================
  function reRenderExpansionPreview(productId, editedItems, overrideColor, logoKey) {
    var config = window.ExpansionConfig.getProduct(productId);
    if (!config) return Promise.resolve(null);

    var variantData = window.EXPANSION_VARIANT_DATA && window.EXPANSION_VARIANT_DATA[productId];
    if (!variantData) return Promise.resolve(null);

    // Use explicit color if provided, then product's saved color, then color picker
    var currentColor = overrideColor
      || (window.EXPANSION_SELECTED_COLOR && window.EXPANSION_SELECTED_COLOR[productId])
      || $(".canvas-color-picker .color-btn.selected").data("title") || "Grå";
    var colorData = variantData[currentColor];
    if (!colorData) {
      var hoodieColors = [...new Set(window.variantData.map(function(v) { return v.color; }))];
      var expColors = Object.keys(variantData);
      var cidx = hoodieColors.indexOf(currentColor);
      if (cidx >= 0 && cidx < expColors.length) {
        currentColor = expColors[cidx];
        colorData = variantData[currentColor];
      }
    }
    if (!colorData) colorData = variantData[Object.keys(variantData)[0]];
    if (!colorData) return Promise.resolve(null);

    // As in exportPreviewForCurrentColor. overrideColor is the editor's swatch,
    // which on a white-only card is the garment colour.
    var garment = garmentColorFor(logoKey || productId, overrideColor);
    if (garment) {
      colorData = window.VariantLogoUtil.garmentMockup(productId, garment) || colorData;
      currentColor = window.VariantLogoUtil.garmentArtColor();
    }

    var items = editedItems
      || (window.EXPANSION_EDITED_ITEMS && window.EXPANSION_EDITED_ITEMS[productId])
      || window.EXPANSION_DESIGN_ITEMS
      || [];
    var result = {};
    var textColor = window.VariantLogoUtil.getTextColor(garment || currentColor);

    return buildEditedItemOverrides(productId, currentColor, items, config, logoKey).then(function(imageOverrides) {
      return config.views.reduce(function(chain, viewName) {
        return chain.then(function() {
          return renderViewFromEditedItems(viewName, config, colorData, items, productId, textColor, imageOverrides, logoKey)
            .then(function(dataUrl) {
              result[viewName] = dataUrl;
            });
        });
      }, Promise.resolve()).then(function() {
        return result;
      });
    });
  }

  // =========================================================================
  // renderSingleColumnTeamWallFromText
  // Same as renderSingleColumnTeamWall but accepts text parameter
  // instead of reading from #back_middle_input. Used by zip mode editor.
  // =========================================================================
  function renderSingleColumnTeamWallFromText(inputText, destBox, explicitTextColor) {
    return new Promise(function(resolve) {
      if (!inputText || !inputText.trim()) { resolve(null); return; }

      var names = inputText
        .split(/\r?\n/)
        .filter(function(n) { return n.trim(); })
        .map(function(n) { return n.trim().toUpperCase(); });

      if (!names.length) { resolve(null); return; }

      // Custom name wall color (advanced option) wins over the automatic
      // per-garment-color text color
      var customColor = (window.TeamWall && window.TeamWall.getCustomTextColor)
        ? window.TeamWall.getCustomTextColor() : null;
      var textColor = customColor || explicitTextColor
        || ((window.TeamWall && window.TeamWall.getTextColor) ? window.TeamWall.getTextColor() : "#000000");

      var fontFamily = "'Bebas Neue', sans-serif";
      var fontWeight = "400";

      var renderScale = 4;
      var canvas = document.createElement("canvas");
      canvas.width = destBox.width * renderScale * SCALE;
      canvas.height = destBox.height * renderScale * SCALE;
      var ctx = canvas.getContext("2d");
      ctx.scale(renderScale * SCALE, renderScale * SCALE);

      var maxFontSize = 18;
      var minFontSize = 8;
      var lineHeightFactor = 1.2;
      var topMargin = 10;
      var fontSize = maxFontSize;

      function tryFit() {
        ctx.font = fontWeight + " " + fontSize + "px " + fontFamily;
        var lineHeight = fontSize * lineHeightFactor;
        var totalHeight = names.length * lineHeight + topMargin;
        if (totalHeight > destBox.height && fontSize > minFontSize) return false;
        for (var i = 0; i < names.length; i++) {
          if (ctx.measureText(names[i]).width > destBox.width * 0.9) return false;
        }
        return true;
      }

      while (!tryFit() && fontSize > minFontSize) { fontSize--; }

      function doRender() {
        ctx.clearRect(0, 0, destBox.width, destBox.height);
        ctx.fillStyle = textColor;
        ctx.textBaseline = "top";
        ctx.textAlign = "center";
        ctx.font = fontWeight + " " + fontSize + "px " + fontFamily;

        var lineHeight = fontSize * lineHeightFactor;
        var totalHeight = names.length * lineHeight;
        var startY = topMargin;
        if (totalHeight + topMargin < destBox.height) {
          startY = (destBox.height - totalHeight) / 2;
        }

        // Golden top names: first N lines of this list
        var goldStyle = (window.TeamWall && window.TeamWall.getGoldStyle)
          ? window.TeamWall.getGoldStyle() : null;

        for (var i = 0; i < names.length; i++) {
          ctx.fillStyle = (goldStyle && i < goldStyle.goldTopN) ? goldStyle.goldColor : textColor;
          ctx.fillText(names[i], destBox.width / 2, startY + i * lineHeight);
        }
        resolve(canvas);
      }

      var fontSpec = fontWeight + " " + fontSize + "px " + fontFamily;
      if (document.fonts && document.fonts.load) {
        document.fonts.load(fontSpec).then(doRender).catch(doRender);
      } else {
        doRender();
      }
    });
  }

  // =========================================================================
  // renderHoodTextTight
  // Renders hood text with the same font sizing as the hoodie editor (font 60,
  // maxWidth = source hood box width - 10), on a tight canvas. Draws the result
  // centered in destBox, scaled proportionally from source to dest box.
  // This ensures the text appears at the same relative size on expansion products.
  // =========================================================================
  function renderHoodTextTight(text, destBox, explicitTextColor, offsetFromCenter, sourceBoxName) {
    return new Promise(function(resolve) {
      if (!text) { resolve(null); return; }

      var textColor = explicitTextColor
        || ((window.TeamWall && window.TeamWall.getTextColor) ? window.TeamWall.getTextColor() : "#000000");

      // Get hoodie source hood box for font sizing reference
      var sourceBox = window.ProductConfig
        ? window.ProductConfig.getBoxByName("hoodie", sourceBoxName || "hood_right")
        : null;
      if (!sourceBox) sourceBox = { width: 120, height: 120 };

      var fontFamily = "'Bebas Neue', sans-serif";
      var fontWeight = "400";

      // Same font sizing as renderHoodText in image-upload.js
      var mCvs = document.createElement("canvas");
      mCvs.width = 1; mCvs.height = 1;
      var mCtx = mCvs.getContext("2d");
      var maxWidth = sourceBox.width - 10;
      var fontSize = 60;
      mCtx.font = fontWeight + " " + fontSize + "px " + fontFamily;
      while (mCtx.measureText(text).width > maxWidth && fontSize > 10) {
        fontSize--;
        mCtx.font = fontWeight + " " + fontSize + "px " + fontFamily;
      }

      // Tight text measurement
      var metrics = mCtx.measureText(text);
      var textW = metrics.width;
      var textH = fontSize;
      if (metrics.actualBoundingBoxAscent !== undefined) {
        textH = metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent;
      }
      var padPx = 8;
      var cvsW = Math.ceil(textW) + padPx * 2;
      var cvsH = Math.ceil(textH) + padPx * 2;

      // Render at high resolution
      var renderScale = 4;
      var canvas = document.createElement("canvas");
      canvas.width = cvsW * renderScale * SCALE;
      canvas.height = cvsH * renderScale * SCALE;
      var ctx = canvas.getContext("2d");
      ctx.scale(renderScale * SCALE, renderScale * SCALE);

      function doRender() {
        ctx.clearRect(0, 0, cvsW, cvsH);
        ctx.font = fontWeight + " " + fontSize + "px " + fontFamily;
        ctx.fillStyle = textColor;
        ctx.textBaseline = "middle";
        ctx.textAlign = "center";
        ctx.fillText(text, cvsW / 2, cvsH / 2);

        // Return canvas + draw info (same absolute size as hoodie — no proportional scaling)
        var ofc = offsetFromCenter || { x: 0, y: 0 };
        var drawW = cvsW * SCALE;
        var drawH = cvsH * SCALE;
        var drawX = (destBox.x + destBox.width / 2 + ofc.x) * SCALE - drawW / 2;
        var drawY = (destBox.y + destBox.height / 2 + ofc.y) * SCALE - drawH / 2;

        resolve({ canvas: canvas, drawX: drawX, drawY: drawY, drawW: drawW, drawH: drawH });
      }

      var fontSpec = fontWeight + " " + fontSize + "px " + fontFamily;
      if (document.fonts && document.fonts.load) {
        document.fonts.load(fontSpec).then(doRender).catch(doRender);
      } else {
        doRender();
      }
    });
  }

  // =========================================================================
  // renderPlaceholderText
  // Renders a single line of placeholder text (e.g. "ETTERNAVN") centered
  // in a destination box. Uses Bebas Neue font and TeamWall text color.
  // Returns a canvas (not data URL) via Promise.
  // =========================================================================
  function renderPlaceholderText(text, destBox, explicitTextColor) {
    return new Promise(function(resolve) {
      if (!text) { resolve(null); return; }

      var textColor = explicitTextColor
        || ((window.TeamWall && window.TeamWall.getTextColor) ? window.TeamWall.getTextColor() : "#000000");

      var fontFamily = "'Bebas Neue', sans-serif";
      var fontWeight = "400";

      var renderScale = 4;
      var canvasH = destBox.height;
      var canvas = document.createElement("canvas");
      canvas.width = destBox.width * renderScale * SCALE;
      canvas.height = canvasH * renderScale * SCALE;
      var ctx = canvas.getContext("2d");
      ctx.scale(renderScale * SCALE, renderScale * SCALE);

      // Cap height = box height (Bebas Neue cap ratio = 0.7)
      var fontSize = Math.floor(destBox.height / 0.7);
      var minFontSize = 8;

      function tooWide() {
        ctx.font = fontWeight + " " + fontSize + "px " + fontFamily;
        return ctx.measureText(text).width > destBox.width * 0.95;
      }

      while (tooWide() && fontSize > minFontSize) { fontSize--; }

      function doRender() {
        ctx.clearRect(0, 0, destBox.width, canvasH);
        ctx.fillStyle = textColor;
        ctx.textBaseline = "alphabetic";
        ctx.textAlign = "center";
        ctx.font = fontWeight + " " + fontSize + "px " + fontFamily;
        var capH = fontSize * 0.7;
        ctx.fillText(text, destBox.width / 2, (destBox.height + capH) / 2);
        resolve(canvas);
      }

      var fontSpec = fontWeight + " " + fontSize + "px " + fontFamily;
      if (document.fonts && document.fonts.load) {
        document.fonts.load(fontSpec).then(doRender).catch(doRender);
      } else {
        doRender();
      }
    });
  }

  // Export
  window.ExpansionRenderer = {
    exportProductImages: exportProductImages,
    exportPreviewForCurrentColor: exportPreviewForCurrentColor,
    buildExpansionDesignItems: buildExpansionDesignItems,
    buildEditedItemOverrides: buildEditedItemOverrides,
    expansionSlots: expansionSlots,
    syntheticEditedItems: syntheticEditedItems,
    // Hydrate-then-resolve a product's own per-colour artwork for named boxes.
    // Exported 2026-08-17 for the draft restore, which needs the same three
    // steps in the same order (ensure the keys, fall back to a sibling
    // colourway, warn when a slot is in the store but unresolvable) and had no
    // business growing a third copy of them.
    loadOwnBoxImages: loadOwnBoxImages,
    renderSingleColumnTeamWall: renderSingleColumnTeamWall,
    renderSingleColumnTeamWallFromText: renderSingleColumnTeamWallFromText,
    renderPlaceholderText: renderPlaceholderText,
    renderHoodTextTight: renderHoodTextTight,
    // A card's hood mode and text for one side, with the duplicate-to-original
    // and hoodie fallbacks, so the editor and the colour swatch read them the
    // way the card is drawn.
    hoodSideMode: hoodSideMode,
    hoodSideText: getHoodSideText,
    exportFromEditedItems: exportFromEditedItems,
    reRenderExpansionPreview: reRenderExpansionPreview
  };

})(window);
